<template>
  <div>
    <RetailAnalyticsHeader @date-change="onDateChange">
      <div class="flex flex-wrap items-center gap-2">
        <SecondaryButton v-if="isOrganizationAdmin" :loading="isExporting" @click="exportCSV">
          <DownloadIcon v-if="!isExporting" class="h-4 w-4 mr-2" />
          {{ t("export_button") }}
        </SecondaryButton>
        <SecondaryButton v-if="storeId && stats && backToStoresBtn" @click="storeIdSelected(null)">
          {{ t("back") }}
        </SecondaryButton>
        <template v-else-if="!storeId && hasFilters">
          <SearchBarFilter
            v-for="(filterOptions, filterName, filterIdx) in availableFilters"
            :key="filterName"
            :filterName="filterName"
            :filterIdx="filterIdx"
            :available-filters="availableFilters"
            :selected-filters="selectedFilters"
            @updateFilterSearch="updateFilterSearch"
          />
        </template>
      </div>
    </RetailAnalyticsHeader>

    <div v-if="isLoading" class="flex justify-center items-center">
      <Loading />
    </div>
    <div v-else>
      <div v-if="storeId && stats">
        <SellerAnalytics
          v-if="storeId && stats"
          :currentMonth="currentMonth"
          :stats="stats"
          :total="total"
          :showGrossSalesCount="hasSeatsOrFurniture"
          :order="order"
          @sort="onSort"
        />
      </div>
      <template v-else-if="stats">
        <p class="mb-3 text-xs text-gray-400 italic">
          {{ t("click_store_hint") }}
        </p>
        <StoreAnalytics
          :currentMonth="currentMonth"
          :stats="stats"
          :total="total"
          :accessibleStoreIds="accessibleStoreIds"
          :showGrossSalesCount="hasSeatsOrFurniture"
          :showTrainingCompletion="hasTrainingCompletion"
          :order="order"
          @store-id-selected="storeIdSelected"
          @sort="onSort"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters, useStore } from "vuex";
import { Loading, SecondaryButton, SearchBarFilter, download } from "@estaly/ui";
import { DownloadIcon } from "@heroicons/vue/outline";
import { getQueryParams, setQueryParams } from "@estaly/ui/src/utils/query_params";
import RetailAnalyticsHeader from "@/components/analytics/retail/RetailAnalyticsHeader.vue";
import StoreAnalytics from "@/components/analytics/retail/StoreAnalytics.vue";
import SellerAnalytics from "@/components/analytics/retail/SellerAnalytics.vue";
import AnalyticsService from "@/api/services/analytics";
import { useServerSort } from "@/composables/useServerSort";

export default {
  components: {
    RetailAnalyticsHeader,
    StoreAnalytics,
    SellerAnalytics,
    Loading,
    SecondaryButton,
    SearchBarFilter,
    DownloadIcon,
  },

  setup() {
    const { t } = useI18n();
    const { getters } = useStore();
    const hasExternalPosOnlySubscription = computed(() => getters["businessVerticals/hasExternalPosOnlySubscription"]);
    const defaultSortColumn = computed(() =>
      getters["businessVerticals/hasSeatsOrFurniture"] ? "gross_sales_count" : "sales_count",
    );
    const { effectiveSortColumn, sortDirection, order, handleSort, resetSort } = useServerSort(defaultSortColumn);
    return { t, hasExternalPosOnlySubscription, effectiveSortColumn, sortDirection, order, handleSort, resetSort };
  },

  data: () => {
    return {
      isLoading: false,
      isExporting: false,
      storeId: null,
      startDate: null,
      endDate: null,
      currentMonth: "",
      stats: null,
      total: null,
      selectedFilters: {
        ownershipType: [],
        region: [],
      },
    };
  },

  computed: {
    ...mapGetters("store", ["selectedStoreId", "getOwnershipTypes"]),
    ...mapGetters("organization", ["regions"]),
    ...mapGetters("auth", ["accessibleStoreIds", "isOrganizationAdmin"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),

    backToStoresBtn() {
      return !this.selectedStoreId;
    },

    ownershipTypeFilterOptions() {
      return this.getOwnershipTypes().map((value) => ({
        value,
        label: this.$t(`store.ownership_type.${value}`),
      }));
    },

    regionFilterOptions() {
      if (!this.hasSeatsOrFurniture) return [];
      return this.regions.map((region) => ({ value: region, label: region }));
    },

    availableFilters() {
      const filters = {
        ownershipType: this.ownershipTypeFilterOptions,
      };
      if (this.regionFilterOptions.length) {
        filters.region = this.regionFilterOptions;
      }
      return filters;
    },

    // Rates only exist for the user's stores where a training module applies
    hasTrainingCompletion() {
      return (this.stats || []).some((stat) => stat.trainingCompletionRate != null);
    },

    hasFilters() {
      return Object.keys(this.availableFilters).length > 0;
    },

    exportIncludeColumns() {
      const columns = [];
      if (this.hasSeatsOrFurniture) {
        columns.push("gross_sales");
        columns.push("compliance");
        columns.push("leads");
      }
      if (!this.hasExternalPosOnlySubscription) {
        columns.push("leads_processing");
      }
      return columns;
    },
  },

  created() {
    this.fetchOrganization();
    if (this.selectedStoreId) {
      this.storeId = this.selectedStoreId;
    }
    const region = this.$route.query.region;
    if (region) {
      this.selectedFilters.region = [region];
    }
  },

  methods: {
    ...mapActions("organization", { fetchOrganization: "fetch" }),
    ...mapActions("notifications", ["notify"]),

    async exportCSV() {
      if (!this.startDate || !this.endDate) return;
      this.isExporting = true;
      try {
        let response;
        if (this.storeId) {
          response = await AnalyticsService.exportRetailSellers(
            this.storeId,
            this.startDate,
            this.endDate,
            this.effectiveSortColumn,
            this.sortDirection,
            this.exportIncludeColumns,
          );
        } else {
          response = await AnalyticsService.exportRetailStores(
            this.startDate,
            this.endDate,
            this.selectedFilters.ownershipType,
            this.selectedFilters.region,
            this.effectiveSortColumn,
            this.sortDirection,
            this.exportIncludeColumns,
          );
        }

        if (response?.status === 204) {
          return this.notify({ category: "simple", type: "error", title: this.t("no_data_to_export") });
        }

        if (!response?.success) return;

        const filename = this.storeId ? "retail_analytics_sellers.csv" : "retail_analytics_stores.csv";
        download(response.data, filename);
      } finally {
        this.isExporting = false;
      }
    },

    loadStoreIdFromQueryParams() {
      const storeId = getQueryParams().storeId;
      if (storeId) this.storeId = storeId;
    },

    onDateChange({ start, end, currentMonth }) {
      this.startDate = start;
      this.endDate = end;
      this.currentMonth = currentMonth;
      setQueryParams({ tab: "retail", storeId: this.storeId });
      this.getAnalytics();
    },

    async getAnalytics() {
      if (!this.startDate || !this.endDate) return;
      if (this.isLoading) return;

      this.isLoading = true;
      let response;

      if (this.storeId) {
        response = await AnalyticsService.getRetailSellersData(
          this.storeId,
          this.startDate,
          this.endDate,
          this.effectiveSortColumn,
          this.sortDirection,
        );
      } else {
        response = await AnalyticsService.getRetailStoresData(
          this.startDate,
          this.endDate,
          this.selectedFilters.ownershipType,
          this.selectedFilters.region,
          this.effectiveSortColumn,
          this.sortDirection,
        );
      }

      this.isLoading = false;

      if (response?.success) {
        const data = response.data;
        this.stats = data.stats;
        this.total = data.total;
      }
    },

    storeIdSelected(storeId) {
      if (this.storeId === storeId) return;

      this.storeId = storeId;
      this.resetSort();
      setQueryParams({ tab: "retail", storeId: this.storeId });
      this.getAnalytics();
    },

    onSort(column) {
      if (this.isLoading) return;
      this.handleSort(column);
      this.getAnalytics();
    },

    updateFilterSearch(filterName, value) {
      this.selectedFilters[filterName] = value;
      this.getAnalytics();
    },
  },

  watch: {
    "$route.query"() {
      this.loadStoreIdFromQueryParams();
    },
  },
};
</script>

<i18n src="./RetailAnalytics.i18n.json"></i18n>
